import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { ComponentBase } from 'src/app/components/core/_infrastructure/classes/componentBase';
import { NotificationService } from 'src/app/components/core/_infrastructure/classes/services/notification.service';
import { AuthService } from 'src/app/components/core/_infrastructure/classes/services/auth.service';
import { ExceptionManager } from 'src/app/components/core/_infrastructure/classes/handlers/exceptionManager';
import { FormGroup } from '@angular/forms';
import { Tc } from '../../../../../core/_infrastructure/types/common';
import { PeriodoAmbitoCaCamViewModel, PeriodoAmbitoProvinciaCamViewModel, PeriodoAmbitoViewModel } from '../ambito.types';
import { ConfirmationService } from 'primeng/api';

@Component({
    selector: 'app-ambito-edit',
    templateUrl: './ambito-edit.component.html',
    styleUrls: ['./ambito-edit.component.scss'],
})
export class PeriodoAmbitoEditComponent extends ComponentBase implements OnChanges, OnInit {
    @Input() ambitoPeriodo: PeriodoAmbitoViewModel;
    @Input() formGroup: FormGroup;
    @Input() comunidades: Tc[];
    @Input() provincias: Tc[];
    @Input() esPeriodoFinalizado = false;
    @Input() esVigenteConSolicitudes = false;
    @Output() selectComunidadEmitter = new EventEmitter<number>();
    @Output() selectAmbitoEmitter = new EventEmitter<number>();
    @Output() addComunidadEmitter = new EventEmitter<PeriodoAmbitoCaCamViewModel>();
    @Output() deleteComunidadEmitter = new EventEmitter<PeriodoAmbitoCaCamViewModel>();
    @Output() addProvinciaEmitter = new EventEmitter<PeriodoAmbitoProvinciaCamViewModel>();
    @Output() deleteProvinciaEmitter = new EventEmitter<PeriodoAmbitoProvinciaCamViewModel>();

    IdComunidad: number;
    campoObligatorio: boolean;

    isCollapsedCabecera = false;
    isCollapsedComAutProv = false;

    constructor(
        protected notificationService: NotificationService,
        protected authService: AuthService,
        protected exceptionHandler: ExceptionManager,
        private confirmationService: ConfirmationService,
    ) {
        super(authService, notificationService, exceptionHandler);
    }

    ngOnInit(): void {
        this.updateForm();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes?.ambitoPeriodo?.currentValue) {
            this.ambitoPeriodo = { ...changes.ambitoPeriodo.currentValue };
            this.updateForm();
        }
        this.checkDisable();
    }

    private checkDisable(): void {
        if (this.esPeriodoFinalizado || this.esVigenteConSolicitudes) {
            this.formGroup.controls['AmbitoNacional'].disable();
        }
    }

    /**
     * This functions sets the values in the periodoCam (@Input) into the form.
     */
    private updateForm(): void {
        if (this.ambitoPeriodo) {
            this.formGroup.setValue({
                Id: this.ambitoPeriodo?.Id || 0,
                AmbitoNacional: this.ambitoPeriodo?.AmbitoNacional,
                IdCA: null,
                IdProvincia: null,
            });
        }

        this.form = this.formGroup; // componentBase uses this.form
        this.onSelectAmbito();
    }

    onSelectComunidadAutonoma() {
        const idComAut = this.form.value.IdCA;
        this.IdComunidad = this.form.value.IdCA; //lo guardo en una variable para asignarla al añadir la provincia
        this.selectComunidadEmitter.emit(idComAut);
        this.form.patchValue({
            IdProvincia: null,
        });
    }

    onSelectAmbito() {
        if (this.form.value.AmbitoNacional) {
            if (this.ambitoPeriodo && this.ambitoPeriodo.TPeriodoAmbitoCaCamCollection.length > 0) {
                //mensaje de confirmación
                //TODO rdlf -> eliminar lista de CA y Provincias
                this.confirmationService.confirm({
                    message: '¿Se eliminarán las Comunidades y Provincias, desea continuar?',
                    accept: () => {
                        //this.ambitoPeriodo.TPeriodoAmbitoCaCamCollection = [];
                        //this.ambitoPeriodo.TPeriodoAmbitoProvinciaCamCollection = [];
                        this.form.controls['IdCA'].disable();
                        this.form.controls['IdProvincia'].disable();
                        this.campoObligatorio = false;
                        this.form.patchValue({
                            IdCA: null,
                            IdProvincia: null,
                        });
                        this.selectAmbitoEmitter.emit();
                    },
                    reject: () => {
                        this.campoObligatorio = true;
                        this.form.patchValue({
                            AmbitoNacional: false,
                        });
                        return;
                    },
                });
            } else {
                this.form.controls['IdCA'].disable();
                this.form.controls['IdProvincia'].disable();
                this.campoObligatorio = false;
                this.form.patchValue({
                    IdCA: null,
                    IdProvincia: null,
                });
            }
        } else {
            if (this.esPeriodoFinalizado || this.esVigenteConSolicitudes) {
                return;
            }
            this.form.controls['IdCA'].enable();
            this.form.controls['IdProvincia'].enable();
            this.campoObligatorio = true;
        }
    }

    onAddComunidad() {
        const comunidad = new PeriodoAmbitoCaCamViewModel();
        comunidad.IdCA = this.form.value.IdCA;
        if (comunidad.IdCA!) {
            this.addComunidadEmitter.emit(comunidad);
        }
    }

    onDeleteComunidad(item: PeriodoAmbitoCaCamViewModel) {
        this.deleteComunidadEmitter.emit(item);
    }

    onAddProvincia() {
        const provincia = new PeriodoAmbitoProvinciaCamViewModel();
        provincia.IdProvincia = this.form.value.IdProvincia;
        provincia.IdComunidad = this.IdComunidad;
        if (provincia.IdProvincia!) {
            this.addProvinciaEmitter.emit(provincia);
        }
    }

    onDeleteProvincia(item: PeriodoAmbitoProvinciaCamViewModel) {
        this.deleteProvinciaEmitter.emit(item);
    }

    showButtonDeleteComunidad(item: PeriodoAmbitoCaCamViewModel): boolean {
        if (this.esPeriodoFinalizado) {
            return false;
        }
        // Mostramos el botón de borrar la CA cuando no tiene Id, esto es, para los elementos seleccionados en la ventana de diálogo, no para los que vienen ya de BBDD que si tienen Id.
        if (this.esVigenteConSolicitudes) {
            return !item.Id;
        }
        return true;
    }

    showButtonDeleteProvincia(item: PeriodoAmbitoProvinciaCamViewModel): boolean {
        if (this.esPeriodoFinalizado) {
            return false;
        }
        // Mostramos el botón de borrar la Provincia cuando no tiene Id, esto es, para los elementos seleccionados en la ventana de diálogo, no para los que vienen ya de BBDD que si tienen Id.
        if (this.esVigenteConSolicitudes) {
            return !item.Id;
        }
        return true;
    }
}
